import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { inr, today, formatApiError, hasWrite } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Landmark, Plus, FileDown, FileText, Pencil, BookOpen, Trash2 } from "lucide-react";
import { toast } from "sonner";

const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const flabel = "block text-sm font-medium mb-1.5";
const dateField = "h-9 px-2 rounded-md border border-input bg-card text-sm num focus:outline-none focus:ring-2 focus:ring-ring/40";

export default function CapitalAccounts() {
  const { user } = useAuth();
  const canWrite = hasWrite(user);
  const [list, setList] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const emptyCf = { name: "", opening_balance: "", opening_type: "DR" };
  const [cf, setCf] = useState(emptyCf);
  const [editing, setEditing] = useState(null); // party being edited (or "new")
  const [ledgerFor, setLedgerFor] = useState(null); // capital account whose entries are open
  const [entries, setEntries] = useState([]);
  const emptyEf = { date: today(), particulars: "", type: "CR", amount: "" };
  const [ef, setEf] = useState(emptyEf);

  const loadEntries = (p) => api.get("/ledger-entries", { params: { scope: "capital", target_id: p.id } })
    .then((r) => setEntries(r.data || [])).catch(() => setEntries([]));

  const openLedger = (p) => { setLedgerFor(p); setEf(emptyEf); loadEntries(p); };

  const addEntry = async () => {
    const amt = Math.abs(parseFloat(ef.amount) || 0);
    if (!ef.date) return toast.error("Pick a date");
    if (amt <= 0) return toast.error("Enter an amount");
    const payload = { scope: "capital", target_id: ledgerFor.id, date: ef.date, particulars: ef.particulars,
      debit: ef.type === "DR" ? amt : 0, credit: ef.type === "CR" ? amt : 0 };
    try {
      await api.post("/ledger-entries", payload);
      toast.success("Entry added");
      setEf({ ...emptyEf, date: ef.date });
      loadEntries(ledgerFor); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delEntry = async (mid) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await api.delete(`/ledger-entries/${mid}`); loadEntries(ledgerFor); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = () => api.get("/capital-accounts").then((r) => setList(r.data || [])).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setCf(emptyCf); setEditing("new"); };
  const openEdit = (p) => {
    setCf({ name: p.name, opening_balance: p.opening_balance ? Math.abs(p.opening_balance) : "", opening_type: p.opening_balance < 0 ? "CR" : "DR" });
    setEditing(p);
  };

  const save = async () => {
    const mag = Math.abs(parseFloat(cf.opening_balance) || 0);
    const payload = { name: cf.name.trim(), opening_balance: mag, opening_type: cf.opening_type };
    try {
      if (editing === "new") {
        if (!payload.name) return toast.error("Partner name is required");
        await api.post("/capital-accounts", payload);
        toast.success("Capital withdrawings account created");
      } else {
        await api.put(`/capital-accounts/${editing.id}`, payload);
        toast.success("Opening balance updated");
      }
      setEditing(null); setCf(emptyCf); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const download = async (p, fmt) => {
    try {
      const params = { format: fmt };
      if (start) params.start = start;
      if (end) params.end = end;
      const res = await api.get(`/capital-accounts/${p.id}/statement`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `capital_${p.name.replace(/\s/g, "_")}.${fmt}`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download statement"); }
  };

  const fmtBal = (p) => `${inr(p.balance_abs)}${p.balance_label ? " " + p.balance_label : ""}`;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        <PageHeader title="Capital Withdrawings" subtitle="Partner capital — deposits (CR) and withdrawals (DR). Visible to accountants and admins only">
          {canWrite && (
            <button data-testid="new-capital-btn" onClick={openNew}
              className="h-9 px-3 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors"><Plus size={15} /> New partner</button>
          )}
        </PageHeader>

        <div className="flex flex-wrap items-end gap-3 mb-5">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Statement from</label>
            <input data-testid="capital-start" type="date" className={dateField} value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">to</label>
            <input data-testid="capital-end" type="date" className={dateField} value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          {(start || end) && <button onClick={() => { setStart(""); setEnd(""); }} className="h-9 px-3 rounded-md border border-input text-sm hover:bg-muted">Clear</button>}
          <p className="text-xs text-muted-foreground">Downloads use this date range. Leave blank for all transactions.</p>
        </div>

        <div className="space-y-3">
          {list.length === 0 && (
            <div className="text-center py-16 text-muted-foreground">
              <Landmark className="mx-auto mb-3 opacity-40" size={32} />
              No capital accounts yet. Click <b className="text-foreground">New partner</b> to add one.
            </div>
          )}
          {list.map((p) => (
            <div key={p.id} data-testid={`capital-row-${p.name.replace(/\s/g, "-").toLowerCase()}`}
              className="rounded-lg border border-border bg-card p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 font-display text-lg font-semibold"><Landmark size={16} className="text-accent" /> {p.name}</div>
                <div className="text-xs text-muted-foreground num mt-0.5">
                  Opening: {inr(Math.abs(p.opening_balance))} {p.opening_balance < 0 ? "CR" : p.opening_balance > 0 ? "DR" : ""}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Current balance</div>
                  <div className={`num text-lg font-bold ${p.balance_label === "CR" ? "text-[hsl(var(--positive))]" : "text-destructive"}`} data-testid={`capital-balance-${p.name.replace(/\s/g, "-").toLowerCase()}`}>{fmtBal(p)}</div>
                </div>
                <div className="flex flex-col gap-1.5">
                  {canWrite && (
                    <button data-testid={`capital-edit-${p.name.replace(/\s/g, "-").toLowerCase()}`} onClick={() => openEdit(p)}
                      className="h-8 px-2.5 rounded-md border border-input text-xs flex items-center gap-1 hover:bg-muted"><Pencil size={12} /> Opening</button>
                  )}
                  {canWrite && (
                    <button data-testid={`capital-entries-${p.name.replace(/\s/g, "-").toLowerCase()}`} onClick={() => openLedger(p)}
                      className="h-8 px-2.5 rounded-md border border-input text-xs flex items-center gap-1 hover:bg-muted"><BookOpen size={12} /> Old entries</button>
                  )}
                  <div className="flex gap-1.5">
                    <button data-testid={`capital-pdf-${p.name.replace(/\s/g, "-").toLowerCase()}`} onClick={() => download(p, "pdf")}
                      className="h-8 px-2.5 rounded-md bg-accent/15 text-accent text-xs font-medium flex items-center gap-1 hover:bg-accent/25"><FileText size={12} /> PDF</button>
                    <button data-testid={`capital-csv-${p.name.replace(/\s/g, "-").toLowerCase()}`} onClick={() => download(p, "csv")}
                      className="h-8 px-2.5 rounded-md border border-input text-xs font-medium flex items-center gap-1 hover:bg-muted"><FileDown size={12} /> CSV</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="bg-card">
            <DialogHeader><DialogTitle className="font-display text-2xl">{editing === "new" ? "New capital partner" : `Opening balance · ${editing?.name}`}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {editing === "new" && (
                <div>
                  <label className={flabel}>Partner name <span className="text-destructive">*</span></label>
                  <input data-testid="capital-name" className={field} value={cf.name}
                    onChange={(e) => setCf({ ...cf, name: e.target.value })} placeholder="e.g. JAGGAN JI" />
                </div>
              )}
              <div>
                <label className={flabel}>Opening balance (₹)</label>
                <div className="flex gap-2">
                  <select data-testid="capital-opening-type" className={field + " w-32"} value={cf.opening_type}
                    onChange={(e) => setCf({ ...cf, opening_type: e.target.value })}>
                    <option value="DR">DR (taken)</option>
                    <option value="CR">CR (deposited)</option>
                  </select>
                  <input data-testid="capital-opening" type="number" min="0" className={field + " num flex-1"} value={cf.opening_balance}
                    onChange={(e) => setCf({ ...cf, opening_balance: e.target.value })} placeholder="0" />
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">{cf.opening_type === "CR" ? "Partner had deposited this much (credit)." : "Partner had taken this much (debit)."}</p>
              </div>
            </div>
            <DialogFooter>
              <button data-testid="capital-save" onClick={save}
                className="h-10 px-5 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors">{editing === "new" ? "Create" : "Save"}</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!ledgerFor} onOpenChange={(o) => !o && setLedgerFor(null)}>
          <DialogContent className="bg-card max-h-[90vh] overflow-y-auto sm:max-w-2xl">
            <DialogHeader><DialogTitle className="font-display text-2xl">Old entries · {ledgerFor?.name}</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground -mt-2 mb-1">Add historical capital entries (before your cash book starts). These show on the statement only — they do not touch the cash book.</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end rounded-lg border border-border p-3 bg-muted/30">
              <div><label className="text-xs text-muted-foreground block mb-1">Date</label>
                <input data-testid="capital-entry-date" type="date" className={field + " num"} value={ef.date} onChange={(e) => setEf({ ...ef, date: e.target.value })} /></div>
              <div className="col-span-2"><label className="text-xs text-muted-foreground block mb-1">Particulars</label>
                <input data-testid="capital-entry-particulars" className={field} placeholder="e.g. Cash deposited, Withdrawal…" value={ef.particulars} onChange={(e) => setEf({ ...ef, particulars: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground block mb-1">Type</label>
                <select data-testid="capital-entry-type" className={field} value={ef.type} onChange={(e) => setEf({ ...ef, type: e.target.value })}>
                  <option value="CR">CR (deposited)</option>
                  <option value="DR">DR (taken)</option>
                </select></div>
              <div><label className="text-xs text-muted-foreground block mb-1">Amount (₹)</label>
                <input data-testid="capital-entry-amount" type="number" min="0" step="0.01" className={field + " num"} placeholder="0" value={ef.amount} onChange={(e) => setEf({ ...ef, amount: e.target.value })} /></div>
            </div>
            <div className="flex justify-end mt-2">
              <button data-testid="capital-entry-save" onClick={addEntry} className="h-9 px-4 rounded-md bg-accent text-accent-foreground text-sm font-medium hover:bg-accent/90 transition-colors flex items-center gap-1.5"><Plus size={14} /> Add entry</button>
            </div>
            <div className="mt-3 rounded-lg border border-border divide-y divide-border">
              <div className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-3 px-3 py-2 bg-muted text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <span>Date</span><span>Particulars</span><span className="text-right">DR</span><span className="text-right">CR</span><span></span>
              </div>
              {entries.map((m) => (
                <div key={m.id} data-testid={`capital-entry-row-${m.id}`} className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-3 px-3 py-2 items-center text-sm">
                  <span className="num text-muted-foreground">{m.date}</span>
                  <span className="truncate">{m.particulars || "—"}</span>
                  <span className="num text-right">{m.debit ? inr(m.debit) : "—"}</span>
                  <span className="num text-right text-[hsl(var(--positive))]">{m.credit ? inr(m.credit) : "—"}</span>
                  <button data-testid={`capital-entry-del-${m.id}`} onClick={() => delEntry(m.id)} className="text-muted-foreground hover:text-destructive transition-colors justify-self-end"><Trash2 size={13} /></button>
                </div>
              ))}
              {entries.length === 0 && <div className="px-3 py-6 text-center text-xs text-muted-foreground">No manual entries yet.</div>}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
